/**
 * "지금 어디" — 제때 도착하는지.
 *
 * 서버는 한 시점만 봅니다. 남은 거리를 돌려줄 뿐 "가까워지는 중인지" 는
 * 말하지 않습니다. 그건 관측이 여러 번 쌓여야 나오는 값이고, 서버가
 * 사용자·모임마다 직전 값을 들고 있으려면 상태를 둬야 해서 얻는 것에 비해
 * 비쌉니다. 이 화면은 어차피 주기적으로 다시 묻고 있으니 여기서 잇습니다.
 *
 * 추세 판정
 *   최근 세 번의 남은 거리를 봅니다. 단조 감소면 approaching, 단조 증가면
 *   away, 거의 안 변하면 stationary. GPS 가 몇십 미터씩 튀므로 그보다 작은
 *   변화는 "안 움직였다" 로 봅니다.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { PlaceReviewRepository } from 'repository/placeReviewRepository';
import { unwrapData } from 'common/utils/apiResponse';

export type TraceTrend = 'approaching' | 'away' | 'stationary' | 'arrived' | 'unknown';

export interface TraceLive {
    available: boolean;
    destinationName?: string | null;
    remainingM?: number;
    durationMinutes?: number;
    eta?: string | null;
    delayMinutes?: number;
    onTime?: boolean;
    /** crowdsourced · graph · interpolated · estimated */
    source?: string | null;
    /** high 면 지나간 사람이 있는 구간, low 면 우리가 속도로 계산한 값 */
    confidence?: 'high' | 'low' | null;
}

export interface TraceLiveTarget {
    /** 목적지 좌표. **모르면 안 줘도 됩니다** — 서버가 이 모임 일정에서
     *  아직 안 지난 다음 장소를 찾아 씁니다. 일정 장소의 절반 가까이가
     *  좌표 없이 만들어져 있어(개발 DB 39곳 중 20곳), 화면이 좌표를 알아야만
     *  물을 수 있으면 그 절반에서 카드가 통째로 사라집니다. */
    lat?: number | null;
    lng?: number | null;
    name?: string | null;
    /** ISO 8601. 없으면 지각 판정 없이 소요만 나옵니다 */
    scheduledAt?: string | null;
    /** walking · bicycle · car */
    mode?: string | null;
}

/** GPS 가 이 정도는 예사로 튑니다 — 그보다 작은 변화는 움직임으로 보지 않습니다 */
const NOISE_M = 30;
/** 다시 묻는 주기 */
const POLL_MS = 30_000;

export const useTraceLive = (
    meetingId?: string | null,
    target?: TraceLiveTarget | null,
    enabled = true,
) => {
    const [live, setLive] = useState<TraceLive | null>(null);
    const [trend, setTrend] = useState<TraceTrend>('unknown');
    /** 최근 남은 거리 세 개 */
    const historyRef = useRef<number[]>([]);

    const load = useCallback(async () => {
        if (!meetingId || !target || !enabled) return;
        try {
            const res = await PlaceReviewRepository.getTraceLive(meetingId, {
                /* 없으면 쿼리에서 빠집니다 — 서버가 목적지를 찾습니다 */
                destLat: Number.isFinite(Number(target.lat)) ? Number(target.lat) : undefined,
                destLng: Number.isFinite(Number(target.lng)) ? Number(target.lng) : undefined,
                destName: target.name || undefined,
                scheduledAt: target.scheduledAt || undefined,
                mode: target.mode || undefined,
            });
            const data = unwrapData<TraceLive & { trend?: string }>(res);
            if (!data) return;
            setLive(data);

            if (!data.available || typeof data.remainingM !== 'number') {
                setTrend('unknown');
                return;
            }
            if ((data as any).trend === 'arrived') {
                historyRef.current = [];
                setTrend('arrived');
                return;
            }

            const hist = [...historyRef.current, data.remainingM].slice(-3);
            historyRef.current = hist;
            setTrend(judge(hist));
        } catch {
            /* 판정을 못 했다고 화면이 막히면 안 됩니다 — 그 부분만 비웁니다 */
            setLive(null);
            setTrend('unknown');
        }
    }, [meetingId, target, enabled]);

    /* 목적지가 바뀌면 지난 거리 기록은 의미가 없습니다 */
    useEffect(() => {
        historyRef.current = [];
        setTrend('unknown');
    }, [target?.lat, target?.lng]);

    useEffect(() => {
        if (!enabled || !meetingId || !target) return undefined;
        let cancelled = false;
        const tick = async () => {
            if (cancelled) return;
            await load();
        };
        tick();
        const id = window.setInterval(tick, POLL_MS);
        return () => { cancelled = true; window.clearInterval(id); };
    }, [enabled, meetingId, target, load]);

    return { live, trend, reload: load };
};

/** 남은 거리 흐름으로 추세를 정합니다 */
const judge = (hist: number[]): TraceTrend => {
    if (hist.length < 2) return 'unknown';
    const first = hist[0];
    const last = hist[hist.length - 1];
    const delta = last - first;
    if (Math.abs(delta) < NOISE_M) return 'stationary';
    return delta < 0 ? 'approaching' : 'away';
};
